// frontend/src/socket.ts
import { io, Socket } from 'socket.io-client';
import { ExtendedMessage } from './types';

let socket: Socket | null = null;

export const initializeSocket = (
  isAuthenticated: boolean,
  setIsSocketConnected: (value: boolean) => void,
  setMessages: React.Dispatch<React.SetStateAction<ExtendedMessage[]>>,
  setIsLoading: (value: boolean) => void
): { socket: Socket | null; cleanup: () => void } => {
  if (typeof isAuthenticated !== 'boolean') {
    console.error('Erreur: isAuthenticated doit être un booléen');
    return { socket: null, cleanup: () => {} };
  }
  if (socket) {
    console.warn('Socket déjà initialisé, utilisation de l\'instance existante');
    return { socket, cleanup: () => {} };
  }

  try {
    const token = localStorage.getItem('token');
    if (!token || typeof token !== 'string' || token.trim() === '') {
      console.warn('Aucun token valide trouvé dans localStorage, utilisation des cookies');
    }
    // Utiliser la variable d'environnement pour l'URL WebSocket
    const socketUrl = import.meta.env.VITE_WEBSOCKET_URL || 'wss://typia.app';
    console.log('Tentative de connexion WebSocket à:', socketUrl, 'token:', token || 'via cookies');
    socket = io(socketUrl, {
      withCredentials: true,
      auth: token ? { token } : undefined,
      transports: ['websocket', 'polling'],
    });

    socket.on('connect', () => {
      setIsSocketConnected(true);
      console.log('WebSocket connecté avec succès');
    });

    socket.on('message', (msg: ExtendedMessage) => {
      try {
        if (!msg || typeof msg !== 'object') throw new Error('Message invalide reçu');
        if (typeof msg.content !== 'string') throw new Error('Contenu du message invalide');
        if (msg.sender !== 'ia' && msg.sender !== 'user') throw new Error('Sender invalide');

        console.log('Message reçu via WebSocket:', msg);
        console.log('ConversationId du message reçu:', msg.conversationId);

        setMessages((prev) => {
          const existingIndex = prev.findIndex(
            (m) => m.conversationId === msg.conversationId && m.sender === 'ia' && !m.final
          );

          if (existingIndex !== -1) {
            const updatedMessages = [...prev];
            updatedMessages[existingIndex] = {
              ...updatedMessages[existingIndex],
              content: msg.content,
              final: msg.final ?? false,
              timestamp: msg.timestamp || new Date().toISOString(),
            };
            return updatedMessages;
          }

          return [
            ...prev,
            {
              ...msg,
              final: msg.final ?? false,
              timestamp: msg.timestamp || new Date().toISOString(),
            },
          ];
        });

        if (msg.final) {
          console.log('Message final reçu, désactivation de l\'état de chargement');
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Erreur lors du traitement du message WebSocket:', error);
      }
    });

    socket.on('messageStatus', (status: { status: string; conversationId: string }) => {
      try {
        if (!status || typeof status.status !== 'string' || typeof status.conversationId !== 'string') {
          throw new Error('Statut de message invalide');
        }
        console.log('Message status update:', status);
      } catch (error) {
        console.error('Erreur dans messageStatus:', error);
      }
    });

    socket.on('connect_error', (err) => {
      console.error('Erreur de connexion WebSocket:', err.message);
      setIsSocketConnected(false);
    });

    socket.on('disconnect', () => {
      console.log('WebSocket déconnecté');
      setIsSocketConnected(false);
    });

    const cleanup = () => {
      try {
        if (socket) {
          socket.disconnect();
          socket = null;
          setIsSocketConnected(false);
          console.log('Nettoyage du socket terminé');
        }
      } catch (error) {
        console.error('Erreur lors du nettoyage du socket:', error);
      }
    };

    return { socket, cleanup };
  } catch (error) {
    console.error('Erreur lors de l\'initialisation du WebSocket:', error);
    return { socket: null, cleanup: () => {} };
  }
};

export { socket };